import React, { useState, useEffect } from 'react';
import {
  Minus,
  Plus,
  Utensils,
  ShoppingCart,
  X,
  Check
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
import { toast } from 'react-toastify';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

const ExtraSidesCarouselComponent = () => {
  const dispatch = useDispatch();

  // Local states for UI and selections
  const [selections, setSelections] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [chefComment, setChefComment] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  
  // State that holds the extra sides data fetched from Firestore
  const [firestoreExtrasides, setFirestoreExtrasides] = useState([]);

  // Set up a snapshot listener so any changes (e.g., isAvailable) update our state
  useEffect(() => {
    const db = getFirestore();
    const categoryRef = doc(db, 'menuCategories', 'Extra Sides');
    const unsubscribe = onSnapshot(
      categoryRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setFirestoreExtrasides(data.items);
          console.log(data.items)
        }
      },
      (error) => {
        console.error('Error fetching Firestore extra sides:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Handlers for opening and closing the Extras Modal
  const handleOpenModal = (itemId) => {
    const item = firestoreExtrasides.find(item => item.id === itemId);
    setSelectedItem(item);
    setSelectedItemId(itemId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItemId(null);
    setSelectedItem(null);
  };

  // Handler to add an item to the cart
  const handleAddToCart = (item) => {
    const itemSelections = selections[item.id] || {};
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      name: item.name,
      price: item.price,
      quantity: itemSelections.quantity || 1,
      extras: itemSelections.extras || {},
      chefComment: chefComment
    };
    dispatch(addToCart(cartItem));
    toast.success(`${cartItem.quantity}x ${item.name} added to cart!`, {
      position: "top-right",
      autoClose: 3000
    });
  };

  // Handle quantity adjustments for each item
  const handleQuantityChange = (itemId, delta) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.max(1, (prev[itemId]?.quantity || 1) + delta)
      }
    }));
  };

  // Render a single extra side menu item as a card.
  // We use the data from Firestore (firestoreExtrasides) so that any changes (e.g., isAvailable) are reflected.
  const renderMenuItem = (item) => {
    const itemSelections = selections[item.id] || {};
    const quantity = itemSelections.quantity || 1;
    return (
      <div key={item.id} className="bg-black rounded-xl overflow-hidden shadow-lg border border-red-600">
        <div className="relative">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-48 object-cover"
          />
          <div className="absolute top-4 right-4">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full">
              <span className="bg-red-500 text-white font-medium text-lg py-1 px-3 rounded-full">
                ₦{item.price.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <h3 className="text-red-500 font-bold text-xl">{item.name}</h3>
          <p className="text-gray-400 mt-1 text-sm">{item.description}</p>
          {/* If the item has extra ingredients, render the "Extras" button */}
          {item.extras.ingredients && item.extras.ingredients.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => handleOpenModal(item.id)}
                className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-600 flex items-center justify-center gap-2 transition-colors"
              >
                <Utensils className="w-4 h-4" />
                <span>Extras</span>
              </button>
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2 bg-black rounded-lg p-1">
              <button
                onClick={() => handleQuantityChange(item.id, -1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                disabled={quantity <= 1}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-medium text-red-500">{quantity}</span>
              <button
                onClick={() => handleQuantityChange(item.id, 1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => handleAddToCart(item)}
              className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              disabled={!item.isAvailable}
            >
              <ShoppingCart className="w-4 h-4" />
              <span>{item.isAvailable ? 'Add to Cart' : 'Not Available'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // A scrolling banner text at the top
  const bannerText =
    "Note: Jollof Rice, Fried Rice, and Steamed Rice do NOT include protein. For protein with rice, please visit our Grills menu where you can select a protein and enjoy free sides!";

  return (
    <div className="min-h-screen bg-black text-red-500">
      {/* Scrolling banner */}
      <div className="overflow-hidden whitespace-nowrap bg-red-500">
        <div
          className="inline-block animate-marquee text-white text-lg font-bold py-2"
          style={{ animation: 'marquee 15s linear infinite' }}
        >
          {bannerText}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {firestoreExtrasides.map(item => renderMenuItem(item))}
        </div>
      </div>
      {/* Modal for Ingredient Customization (if applicable) */}
      {isModalOpen && selectedItem && selectedItem.extras.ingredients && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-red-600 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">{selectedItem.name} - Extras</h2>
              <button onClick={handleCloseModal}>
                <X className="w-6 h-6 text-gray-300" />
              </button>
            </div>
            <div className="space-y-4">
              {selectedItem.extras.ingredients.map(ingredient => (
                <div key={ingredient.name} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id={ingredient.name}
                    checked={selections[selectedItem.id]?.extras?.ingredients?.some(i => i.name === ingredient.name) || false}
                    onChange={(e) => {
                      // Handler for ingredient checkbox change
                      // (Implement handleIngredientChange as needed)
                    }}
                    className="w-5 h-5 text-red-500"
                  />
                  <label htmlFor={ingredient.name} className="flex-1 text-gray-300">
                    {ingredient.name} (+₦{ingredient.price})
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={selections[selectedItem.id]?.extras?.ingredients?.find(i => i.name === ingredient.name)?.quantity || 1}
                    onChange={(e) => {
                      // Handler for ingredient quantity change
                      // (Implement handleIngredientQuantityChange as needed)
                    }}
                    className="w-16 text-yellow-500 text-center p-2 bg-black border border-gray-700 rounded"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleCloseModal}
                className="py-2 px-6 bg-red-500 text-white font-bold rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Chef Note Modal */}
      {isCommentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-red-600 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Note for the Chef</h3>
              <button onClick={() => setIsCommentModalOpen(false)} className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={chefComment}
              onChange={(e) => setChefComment(e.target.value)}
              placeholder="Any special requests or preferences?"
              className="w-full h-32 p-3 bg-gray-800 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsCommentModalOpen(false)}
                className="px-6 py-2 border border-gray-600 text-gray-200 hover:bg-gray-800 rounded-lg transition-colors focus:ring-2 focus:ring-red-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (chefComment.trim()) {
                    setIsCommentModalOpen(false);
                  }
                }}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 transition-colors focus:ring-2 focus:ring-red-500"
              >
                <Check className="w-4 h-4" />
                <span>Save Note</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Inline keyframes for marquee animation */}
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

export default ExtraSidesCarouselComponent;
